import { useEffect, useRef, type MutableRefObject } from 'react';
import { getDayFocusTarget, hasValidCenter } from '../logic/mapFocus';
import type { MapCenter } from '../logic/mapFocus';
import useTripStore from '../store/useTripStore';
import {
  useDays,
  useFocusDay,
  useSelectedDay,
  useSetFocusDay,
} from './useTripStoreSelectors';

// What the map remembers about the picks of the user, from one render to the
// next. `useTripMapState` owns them because a new trip clears them.
export interface TripMapRefs {
  hasUserSelectedDay: MutableRefObject<boolean>;
  isFirstMount: MutableRefObject<boolean>;
  initialCenter: MutableRefObject<MapCenter | null>;
}

// Below this zoom a day is too small to see: pan and zoom in on it.
const DAY_MIN_ZOOM = 10;
const DAY_ZOOM = 12;
const COUNTRY_ZOOM = 7;

interface PanToDayParams {
  map: any;
  target: { lat: number; lon: number };
  focusDay: number | null;
  selectedDay: number | null;
  setFocusDay: (day: number | null) => void;
}

function panToDay({
  map,
  target,
  focusDay,
  selectedDay,
  setFocusDay,
}: PanToDayParams) {
  map.panTo({ lat: target.lat, lng: target.lon });

  // A user already zoomed in to street level keeps that zoom across days.
  if (map.getZoom() < DAY_MIN_ZOOM) {
    map.setZoom(DAY_ZOOM);
  }
  if (selectedDay === null && focusDay !== null) {
    setFocusDay(null);
  }
}

// Pan to the picked (or focused) day, else to the country when a day is picked
// that has no position.
export default function useTripMapDayFocus({
  map,
  defaultCenter,
  refs,
}: {
  map: any;
  defaultCenter: MapCenter;
  refs: TripMapRefs;
}) {
  const days = useDays();
  const experiencesMap = useTripStore((state) => state.experiences);
  const hotelsMap = useTripStore((state) => state.hotels);
  const selectedDay = useSelectedDay();
  const focusDay = useFocusDay();
  const setFocusDay = useSetFocusDay();
  const previousSelectedDay = useRef(selectedDay);

  useEffect(() => {
    const previous = previousSelectedDay.current;
    previousSelectedDay.current = selectedDay;

    const dayIndex = selectedDay !== null ? selectedDay : focusDay;

    if (!map || dayIndex === null || !days[dayIndex]) {
      return;
    }

    const target = getDayFocusTarget({
      days,
      dayIndex,
      experiencesMap,
      hotelsMap,
    });
    const isNewDay = previous !== selectedDay && selectedDay !== null;

    if (target && Number.isFinite(target.lat) && Number.isFinite(target.lon)) {
      if (isNewDay || focusDay !== null) {
        refs.hasUserSelectedDay.current = true;
        panToDay({ map, target, focusDay, selectedDay, setFocusDay });
      }
    } else if (hasValidCenter(defaultCenter)) {
      const isInitialFocus =
        refs.isFirstMount.current && !refs.initialCenter.current;

      if (isNewDay || isInitialFocus) {
        map.panTo(defaultCenter);
        map.setZoom(COUNTRY_ZOOM);
      }
    }

    refs.isFirstMount.current = false;
  }, [
    selectedDay,
    focusDay,
    map,
    days,
    experiencesMap,
    hotelsMap,
    defaultCenter,
    setFocusDay,
    refs,
  ]);
}
